import { useTranslation } from '@/hooks/useTranslation';

import { DurationInput } from '@/components/form/inputs/DurationInput';
import { SimpleInput } from '@/components/form/inputs/SimpleInput';
import { ComboboxInput } from '@/components/form/inputs/ComboboxInput';

import { OPERATION_OPTIONS } from '@/features/traceability/data/operationOptions';
import { SectionHeader, FieldRow, type SectionRefs } from './TraceEditPrimitives';
import { TraceEditMaterialSection } from './TraceEditMaterialSection';
import type { Material } from '@/features/traceability/type/Material';
import type { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';

type Props = {
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  refs: Pick<SectionRefs, 'operatorRef' | 'durationTotalRef' | 'durationPieceRef'>;
  materials: Material[];
  traceabilityApiClient: TraceabilityApiClient | null;
};

export function TraceEditGeneralSection({ values, onChange, refs, materials, traceabilityApiClient }: Props) {
  const { t } = useTranslation();

  return (
    <>
      {/* General */}
      <div>
        <SectionHeader title={t('toolView.advanced.sections.general')} />
        <FieldRow>
          <SimpleInput
            ref={refs.operatorRef}
            name="operator"
            label={t('traceability.generalTab.operatorLabel')}
            value={(values['operator'] as string) ?? ''}
            onChange={(val) => onChange('operator', val)}
            required
          />
        </FieldRow>
        <FieldRow>
          <ComboboxInput
            name="operation"
            label={t('traceability.generalTab.operationLabel')}
            value={(values['operation'] as string | null) ?? null}
            onChange={(val) => onChange('operation', val)}
            options={OPERATION_OPTIONS}
            placeholder={t('traceability.generalTab.operationPlaceholder')}
          />
        </FieldRow>
        <FieldRow>
          <DurationInput
            ref={refs.durationTotalRef}
            name="duration_total"
            label={t('traceability.generalTab.totalDurationLabel')}
            value={(values['duration_total'] as string) ?? ''}
            onChange={(val) => onChange('duration_total', val)}
          />
        </FieldRow>
        <FieldRow>
          <DurationInput
            ref={refs.durationPieceRef}
            name="duration_piece"
            label={t('traceability.generalTab.pieceDurationLabel')}
            value={(values['duration_piece'] as string) ?? ''}
            onChange={(val) => onChange('duration_piece', val)}
          />
        </FieldRow>
        <FieldRow>
          <SimpleInput
            name="pieces"
            label={t('traceability.generalTab.piecesLabel')}
            type="number"
            value={(values['pieces'] as number) ?? ''}
            onChange={(val) => onChange('pieces', val)}
            min={0}
            step={1}
          />
        </FieldRow>
        <FieldRow>
          <SimpleInput
            name="volume"
            label={t('traceability.generalTab.volumeLabel')}
            type="number"
            value={(values['volume'] as number) ?? ''}
            onChange={(val) => onChange('volume', val)}
            min={0}
            step={1}
          />
        </FieldRow>
        <FieldRow>
          <SimpleInput
            name="distance"
            label={t('traceability.generalTab.distanceLabel')}
            type="number"
            value={(values['distance'] as number) ?? ''}
            onChange={(val) => onChange('distance', val)}
            min={0}
            step={0.1}
          />
        </FieldRow>
        <FieldRow>
          <SimpleInput
            name="malrond"
            label={t('traceability.generalTab.malroundLabel')}
            type="number"
            value={(values['malrond'] as number) ?? ''}
            onChange={(val) => onChange('malrond', val)}
            placeholder={t('traceability.generalTab.malroundPlaceholder')}
          />
        </FieldRow>
        <FieldRow>
          <div className="grid gap-3 md:grid-cols-2">
            <SimpleInput
              name="ae"
              label={t('traceability.generalTab.cutParameters.aeLabel')}
              type="number"
              value={(values['ae'] as number) ?? ''}
              onChange={(val) => onChange('ae', val)}
              min={0}
              step={0.001}
            />
            <SimpleInput
              name="ap"
              label={t('traceability.generalTab.cutParameters.apLabel')}
              type="number"
              value={(values['ap'] as number) ?? ''}
              onChange={(val) => onChange('ap', val)}
              min={0}
              step={0.001}
            />
            <SimpleInput
              name="vc"
              label={t('traceability.generalTab.cutParameters.vcLabel')}
              type="number"
              value={(values['vc'] as number) ?? ''}
              onChange={(val) => onChange('vc', val)}
              min={0}
              step={0.001}
            />
            <SimpleInput
              name="fz"
              label={t('traceability.generalTab.cutParameters.fzLabel')}
              type="number"
              value={(values['fz'] as number) ?? ''}
              onChange={(val) => onChange('fz', val)}
              min={0}
              step={0.001}
            />
          </div>
        </FieldRow>
      </div>

      <TraceEditMaterialSection
        values={values}
        onChange={onChange}
        materials={materials}
        traceabilityApiClient={traceabilityApiClient}
      />
    </>
  );
}
